import { getLocale, t, formatDate, formatNumber } from '../i18n/index.js';
import { arrowStep } from '../i18n/direction.mjs';
import { bookingEvent, availableSlots, createBooking, dateKey, monthDate, monthKey } from './booking-api.mjs';
import { createTimezonePicker } from './timezone-picker.mjs';

const dialog = document.querySelector('[data-booking-dialog]');
if (dialog && typeof dialog.showModal === 'function') initBooking(dialog);

function initBooking(dialog) {
  const find = name => dialog.querySelector(`[data-booking-${name}]`);
  const form = find('form'), calendar = find('calendar'), times = find('times');
  // Native validity is retained, with messages in the site language rather than
  // the browser's potentially different language.
  form.noValidate = true;
  const field = name => form.elements.namedItem(name);
  const consent = field('terms');
  consent.checked = false;
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const zones = [...new Set([timeZone, 'UTC', ...(Intl.supportedValuesOf?.('timeZone') ?? [])])].sort();
  const state = { zone: timeZone, month: currentMonth(timeZone), day: null, start: null, slots: new Map(), loading: false, loadError: null, submitError: null, controller: null, submitting: false, uncertain: false, confirmed: false, topic: 'consultation', trigger: null, step: 'schedule' };
  function updateSubmit() { find('submit').disabled = state.submitting || state.uncertain || !state.start || !consent.checked; }
  const timezonePicker = createTimezonePicker(dialog, { zones, initialValue: timeZone, referenceDate: () => state.day ? new Date(`${state.day}T12:00:00Z`) : state.month, onChange: value => { state.zone = value; state.month = currentMonth(value); state.day = null; loadSlots(); } });
  let viewportFrame = null, viewportHeight = 0;
  function updateViewport() {
    viewportFrame = null;
    if (!dialog.open) return;
    // iOS keyboards shrink/pan the visual viewport without resizing 100dvh.
    // Center against that visible area while retaining the usual desktop size.
    const height = window.visualViewport?.height ?? window.innerHeight;
    dialog.style.setProperty('--booking-viewport-height', `${height}px`);
    dialog.style.setProperty('--booking-viewport-top', `${window.visualViewport?.offsetTop ?? 0}px`);
    const focused = document.activeElement;
    if (height < viewportHeight && form.contains(focused) && focused.matches('input,textarea')) focused.scrollIntoView({ block: 'nearest' });
    timezonePicker.position();
    viewportHeight = height;
  }
  function queueViewportUpdate() {
    if (dialog.open && viewportFrame === null) viewportFrame = requestAnimationFrame(updateViewport);
  }
  window.visualViewport?.addEventListener('resize', queueViewportUpdate, { passive: true });
  window.visualViewport?.addEventListener('scroll', queueViewportUpdate, { passive: true });
  window.addEventListener('resize', queueViewportUpdate, { passive: true });

  function currentMonth(timeZone) { const [year, month] = dateKey(new Date(), timeZone).split('-').map(Number); return monthDate(year, month - 1); }
  function displayDate(key) { return formatDate(new Date(`${key}T12:00:00Z`), { dateStyle: 'long', timeZone: 'UTC', calendar: 'gregory' }); }
  function displayTime(start) { return formatDate(new Date(start), { timeStyle: 'short', timeZone: state.zone }); }
  function selection() {
    if (!state.start) return t('booking.selectPrompt');
    return t('booking.selection', { date: displayDate(dateKey(new Date(state.start), state.zone)), time: displayTime(state.start), zone: state.zone });
  }
  function status(element, message, error = false) { element.textContent = message; element.dataset.error = String(error); }
  function setStep(step) {
    state.step = step;
    timezonePicker.close();
    for (const name of ['schedule', 'details', 'success']) find(name).hidden = name !== step;
    dialog.querySelectorAll('[data-booking-scroll]').forEach(element => { element.scrollTop = 0; });
    find('success').scrollTop = 0;
  }
  function renderSummary() {
    find('duration').textContent = t('booking.duration', { minutes: formatNumber(bookingEvent.duration) });
    find('summary').textContent = selection();
    find('detail-summary').textContent = selection();
    find('confirmed-time').textContent = selection();
  }
  function renderCalendar() {
    const year = state.month.getUTCFullYear(), month = state.month.getUTCMonth();
    find('month').textContent = formatDate(state.month, { month: 'long', year: 'numeric', timeZone: 'UTC', calendar: 'gregory' });
    find('previous').disabled = monthKey(state.month) <= monthKey(currentMonth(state.zone));
    const locale = new Intl.Locale(getLocale());
    const firstDay = (locale.getWeekInfo?.() ?? locale.weekInfo)?.firstDay ?? 1;
    const firstWeekday = firstDay % 7;
    const fragment = document.createDocumentFragment();
    for (let index = 0; index < 7; index++) {
      const day = document.createElement('span'); day.className = 'an-booking__weekday'; day.setAttribute('aria-hidden', 'true');
      const weekday = monthDate(2023, 0, 1 + (firstWeekday + index) % 7);
      day.textContent = formatDate(weekday, { weekday: calendar.clientWidth < 350 ? 'narrow' : 'short', timeZone: 'UTC', calendar: 'gregory' });
      day.title = formatDate(weekday, { weekday: 'long', timeZone: 'UTC', calendar: 'gregory' }); fragment.append(day);
    }
    const offset = (monthDate(year, month).getUTCDay() - firstWeekday + 7) % 7;
    for (let index = 0; index < offset; index++) { const blank = document.createElement('span'); blank.setAttribute('aria-hidden', 'true'); fragment.append(blank); }
    const lastDay = monthDate(year, month + 1, 0).getUTCDate(), today = dateKey(new Date(), state.zone);
    for (let day = 1; day <= lastDay; day++) {
      const key = monthDate(year, month, day).toISOString().slice(0, 10), count = state.slots.get(key)?.length ?? 0;
      const button = document.createElement('button'); button.type = 'button'; button.className = 'an-booking__date'; button.dataset.date = key;
      button.textContent = formatNumber(day, { useGrouping: false }); button.disabled = state.loading || !count;
      button.setAttribute('aria-label', t('booking.dayLabel', { date: displayDate(key), count })); button.setAttribute('aria-pressed', String(state.day === key));
      button.tabIndex = state.day === key ? 0 : -1; if (key === today) button.setAttribute('aria-current', 'date'); fragment.append(button);
    }
    for (let index = offset + lastDay; index < 42; index++) { const blank = document.createElement('span'); blank.setAttribute('aria-hidden', 'true'); fragment.append(blank); }
    calendar.replaceChildren(fragment);
    if (!calendar.querySelector('button[tabindex="0"]:enabled')) calendar.querySelector('button:enabled')?.setAttribute('tabindex', '0');
    calendar.setAttribute('aria-busy', String(state.loading));
  }
  function renderTimes() {
    const panel = find('times-panel'), skeleton = find('skeleton');
    // Keep the phone's single scrolling surface steady when a request or a
    // shorter slot list replaces the previous one. Desktop slots scroll inside
    // their own fixed pane and do not use this minimum size.
    const phone = window.matchMedia('(max-width: 47.99rem)').matches;
    let reservedSize = phone ? Math.max(parseFloat(panel.style.getPropertyValue('--booking-times-size')) || 0, panel.getBoundingClientRect().height) : 0;
    const placeholderCount = Math.max(16, times.children.length);
    const slots = state.slots.get(state.day) ?? [];
    find('day').textContent = state.day ? displayDate(state.day) : find('month').textContent;
    times.replaceChildren(...slots.map(start => {
      const button = document.createElement('button'); button.type = 'button'; button.className = 'an-booking__time'; button.dataset.start = start;
      button.textContent = displayTime(start); button.setAttribute('aria-pressed', String(state.start === start)); return button;
    }));
    times.hidden = state.loading;
    skeleton.hidden = !state.loading;
    if (state.loading) skeleton.replaceChildren(...Array.from({ length: placeholderCount }, () => document.createElement('span')));
    find('slots-panel').setAttribute('aria-busy', String(state.loading));
    if (phone) reservedSize = Math.max(reservedSize, panel.getBoundingClientRect().height);
    panel.style.setProperty('--booking-times-size', `${reservedSize}px`);
    find('continue').disabled = !state.start || state.loading;
    renderSummary();
  }
  function renderLoadStatus() {
    const message = state.loading ? t('booking.loading') : state.loadError ? t(state.loadError === 'rateLimit' ? 'booking.rateLimit' : 'booking.loadFailed') : state.slots.size ? t('booking.chooseTime') : t('booking.noSlots');
    status(find('load-status'), message, !!state.loadError); find('retry').hidden = !state.loadError;
  }
  async function loadSlots() {
    state.controller?.abort(); const controller = new AbortController(); state.controller = controller;
    state.loading = true; state.loadError = null; state.slots = new Map(); state.start = null;
    renderCalendar(); renderTimes(); renderLoadStatus();
    try {
      const slots = await availableSlots(state.month, state.zone, { signal: controller.signal });
      if (controller !== state.controller || !dialog.open) return;
      state.slots = slots; if (!slots.has(state.day)) state.day = [...slots.keys()].sort()[0] ?? null;
    } catch (error) {
      if (controller !== state.controller || error.kind === 'aborted') return;
      state.loadError = error.kind;
    } finally {
      if (controller === state.controller && dialog.open) { state.loading = false; renderCalendar(); renderTimes(); renderLoadStatus(); }
    }
  }
  function close() { dialog.close(); }
  function open(trigger) {
    state.trigger = trigger;
    if (monthKey(state.month) < monthKey(currentMonth(state.zone))) { state.month = currentMonth(state.zone); state.day = null; }
    state.topic = trigger.dataset.bookingTopic ?? 'consultation';
    if (!['consultation', 'review', 'planning'].includes(state.topic)) state.topic = 'consultation';
    if (state.confirmed) { form.reset(); state.confirmed = false; state.start = null; state.submitError = null; }
    if (!field('topic').value || field('topic').value === state.defaultTopic) field('topic').value = t(`booking.topic.${state.topic}`);
    state.defaultTopic = t(`booking.topic.${state.topic}`);
    dialog.showModal(); document.documentElement.classList.add('an-booking-open'); updateViewport();
    renderSummary();
    if (state.submitting || state.uncertain) { setStep('details'); status(find('submit-status'), t(state.uncertain ? 'booking.uncertain' : 'booking.submitting'), state.uncertain); find('submit').disabled = true; find('back').disabled = true; }
    else { setStep('schedule'); loadSlots(); }
    dialog.querySelector('#booking-heading').focus({ preventScroll: true });
  }
  document.querySelectorAll('[data-cal-link]').forEach(trigger => {
    trigger.setAttribute('aria-haspopup', 'dialog');
    trigger.addEventListener('click', event => { event.preventDefault(); if (!dialog.open) open(trigger); });
  });
  dialog.querySelectorAll('[data-booking-close]').forEach(button => button.addEventListener('click', close));
  dialog.addEventListener('close', () => { timezonePicker.close(); state.controller?.abort(); if (viewportFrame !== null) cancelAnimationFrame(viewportFrame); viewportFrame = null; viewportHeight = 0; document.documentElement.classList.remove('an-booking-open'); state.trigger?.focus({ preventScroll: true }); });
  dialog.addEventListener('click', event => { if (event.target !== dialog) return; const box = dialog.getBoundingClientRect(); if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) close(); });
  calendar.addEventListener('click', event => {
    const button = event.target.closest('[data-date]'); if (!button || button.disabled) return;
    state.day = button.dataset.date; state.start = null; renderCalendar(); renderTimes(); calendar.querySelector(`[data-date="${state.day}"]`)?.focus({ preventScroll: true });
  });
  calendar.addEventListener('keydown', event => {
    if (!event.target.matches('[data-date]') || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault(); const buttons = [...calendar.querySelectorAll('button')]; let index = buttons.indexOf(event.target);
    const direction = document.documentElement.dir;
    let step = event.key === 'ArrowUp' ? -7 : event.key === 'ArrowDown' ? 7 : arrowStep(event.key, direction);
    if (event.key === 'Home' || event.key === 'End') { index = event.key === 'Home' ? 0 : buttons.length - 1; step = event.key === 'Home' ? 1 : -1; }
    else index += step;
    while (index >= 0 && index < buttons.length && buttons[index].disabled) index += step;
    if (buttons[index] && !buttons[index].disabled) { buttons.forEach(button => { button.tabIndex = -1; }); buttons[index].tabIndex = 0; buttons[index].focus(); }
  });
  times.addEventListener('click', event => { const button = event.target.closest('[data-start]'); if (!button || button.disabled || state.loading) return; state.start = button.dataset.start; renderTimes(); times.querySelector(`[data-start="${state.start}"]`)?.focus({ preventScroll: true }); });
  for (const [name, offset] of [['previous', -1], ['next', 1]]) find(name).addEventListener('click', () => { state.month = monthDate(state.month.getUTCFullYear(), state.month.getUTCMonth() + offset); state.day = null; loadSlots(); });
  find('retry').addEventListener('click', loadSlots);
  find('continue').addEventListener('click', () => {
    if (!state.start) return;
    updateSubmit();
    find('back').disabled = state.submitting || state.uncertain;
    setStep('details'); status(find('submit-status'), state.submitError ? t(`booking.${state.submitError}`) : '', !!state.submitError);
    dialog.querySelector('#booking-details-heading').focus({ preventScroll: true });
  });
  find('back').addEventListener('click', () => { state.submitError = null; setStep('schedule'); loadSlots(); });
  form.addEventListener('submit', async event => {
    event.preventDefault(); if (state.submitting || state.uncertain || !state.start) return;
    consent.setCustomValidity(consent.checked ? '' : t('booking.termsRequired'));
    for (const name of ['name', 'email', 'topic']) {
      const input = field(name); input.setCustomValidity('');
      if (!input.value.trim()) input.setCustomValidity(t('booking.required'));
      else if (input.validity.typeMismatch) input.setCustomValidity(t('booking.emailInvalid'));
    }
    if (!form.reportValidity()) return;
    // Dismiss a phone keyboard after valid submission so feedback has the full
    // viewport. Native invalid-field focus/validation remains unchanged.
    if (form.contains(document.activeElement) && document.activeElement.matches('input,textarea')) document.activeElement.blur();
    state.submitting = true; state.submitError = null; find('submit').disabled = true; find('back').disabled = true;
    form.setAttribute('aria-busy', 'true'); status(find('submit-status'), t('booking.submitting'));
    try {
      const booking = await createBooking({ start: state.start, name: field('name').value, email: field('email').value, topic: field('topic').value, notes: field('notes').value, timeZone: state.zone, language: getLocale() });
      state.confirmed = true;
      const pending = booking.status === 'pending';
      state.bookingStatus = booking.status;
      find('success-title').textContent = t(pending ? 'booking.pendingTitle' : 'booking.successTitle');
      find('success-copy').textContent = t(pending ? 'booking.pendingCopy' : 'booking.successCopy');
      const link = find('meeting'); link.hidden = !booking.meetingUrl || pending;
      link.href = booking.meetingUrl || find('fallback').href;
      setStep('success'); renderSummary(); if (dialog.open) dialog.querySelector('#booking-success-heading').focus({ preventScroll: true });
    } catch (error) {
      state.uncertain = !!error.uncertain;
      state.submitError = state.uncertain ? 'uncertain' : error.kind === 'unavailable' ? 'unavailable' : error.kind === 'rateLimit' ? 'rateLimit' : 'bookingFailed';
      status(find('submit-status'), t(`booking.${state.submitError}`), true);
      if (error.kind === 'unavailable') { state.start = null; renderSummary(); }
    } finally {
      state.submitting = false; form.setAttribute('aria-busy', 'false'); updateSubmit(); find('back').disabled = state.uncertain;
    }
  });
  for (const name of ['name', 'email', 'topic']) field(name).addEventListener('input', () => field(name).setCustomValidity(''));
  consent.addEventListener('change', () => { consent.setCustomValidity(''); updateSubmit(); });
  window.addEventListener('an:languagechange', () => {
    if (!dialog.open) return; renderSummary(); renderCalendar(); renderTimes(); renderLoadStatus();
    if (state.submitError) status(find('submit-status'), t(`booking.${state.submitError}`), true);
    if (state.submitting) status(find('submit-status'), t('booking.submitting'));
    if (field('topic').value === state.defaultTopic) field('topic').value = t(`booking.topic.${state.topic}`);
    state.defaultTopic = t(`booking.topic.${state.topic}`);
    if (state.confirmed) {
      const pending = state.bookingStatus === 'pending';
      find('success-title').textContent = t(pending ? 'booking.pendingTitle' : 'booking.successTitle');
      find('success-copy').textContent = t(pending ? 'booking.pendingCopy' : 'booking.successCopy');
    }
    timezonePicker.position();
  });
}
